import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Upload, Camera, Loader2, ArrowLeft, Download, RotateCcw, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Visualiser = () => {
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const res = await axios.get(`${API}/materials`);
      setMaterials(res.data);
      if (res.data.length > 0) setSelectedMaterial(res.data[0]);
    } catch (error) {
      console.error("Failed to fetch materials:", error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    setUploadedFile(file);
    setUploadedImage(URL.createObjectURL(file));
    setResultImage(null);
  };

  const handleGenerate = async () => {
    if (!uploadedFile || !selectedMaterial) {
      toast.error("Please upload a photo and select a stone colour");
      return;
    }

    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("material_name", selectedMaterial.name);
      formData.append("colour_hex", selectedMaterial.colour_hex);

      const res = await axios.post(`${API}/visualise`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });

      setResultImage(`data:image/png;base64,${res.data.image_base64}`);
      toast.success("Visualisation generated!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to generate visualisation";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setUploadedImage(null);
    setUploadedFile(null);
    setResultImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = () => {
    if (!resultImage) return;
    const a = document.createElement("a");
    a.href = resultImage;
    a.download = `sill-visualisation-${Date.now()}.png`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E7E5E4] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0F3D2E] rounded flex items-center justify-center text-white font-medium text-sm">S</div>
            <span className="font-medium text-lg text-[#1C1917]">Stone Sills Direct</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E]">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-[#0F3D2E] text-white py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Sparkles className="w-8 h-8" />
            <h1 className="text-4xl font-medium" data-testid="visualiser-title">Sill Visualiser</h1>
          </div>
          <p className="text-white/80 max-w-xl mx-auto">
            Upload a photo of your window and see how a premium stone sill would look. 
            Powered by AI.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Panel - Controls */}
          <div className="space-y-6">
            {/* Upload */}
            <div className="bg-white border border-[#E7E5E4] p-6 rounded-lg">
              <h2 className="text-lg font-medium mb-4">1. Upload Window Photo</h2>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <Button
                variant="outline"
                className="w-full h-24 border-dashed border-2 hover:border-[#0F3D2E] hover:bg-[#0F3D2E]/5"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-btn"
              >
                <div className="flex flex-col items-center gap-2">
                  {uploadedImage ? (
                    <>
                      <Camera className="w-6 h-6 text-[#0F3D2E]" />
                      <span className="text-sm text-[#0F3D2E]">Change Photo</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-[#57534E]" />
                      <span className="text-sm text-[#57534E]">Click to upload a window photo</span>
                    </>
                  )}
                </div>
              </Button>
            </div>

            {/* Material Selection */}
            <div className="bg-white border border-[#E7E5E4] p-6 rounded-lg">
              <h2 className="text-lg font-medium mb-4">2. Choose Stone Colour</h2>
              <div className="space-y-2">
                {materials.map((mat) => (
                  <button
                    key={mat.id}
                    onClick={() => setSelectedMaterial(mat)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      selectedMaterial?.id === mat.id
                        ? "border-[#0F3D2E] bg-[#0F3D2E]/5 shadow-sm"
                        : "border-[#E7E5E4] hover:border-[#A8A29E]"
                    }`}
                    data-testid={`material-option-${mat.id}`}
                  >
                    {mat.image_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${mat.image_url}`}
                        alt={mat.name}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <span
                        className="w-10 h-10 rounded-full border border-gray-200 flex-shrink-0"
                        style={{ backgroundColor: mat.colour_hex }}
                      />
                    )}
                    <div className="text-left">
                      <p className="font-medium text-sm">{mat.name}</p>
                      <p className="text-xs text-[#57534E]">{mat.price_per_sqm.toFixed(2)}/m2</p>
                    </div>
                    {selectedMaterial?.id === mat.id && (
                      <div className="ml-auto w-2 h-2 rounded-full bg-[#0F3D2E]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={!uploadedFile || !selectedMaterial || generating}
              className="w-full h-12 bg-[#0F3D2E] hover:bg-[#0F3D2E]/90 text-white font-medium"
              data-testid="generate-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating... (up to 60s)
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Visualise My Sill
                </>
              )}
            </Button>

            {resultImage && (
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleReset} data-testid="reset-btn">
                  <RotateCcw className="w-4 h-4 mr-2" /> Start Over
                </Button>
                <Button variant="outline" className="flex-1" onClick={handleDownload} data-testid="download-btn">
                  <Download className="w-4 h-4 mr-2" /> Download
                </Button>
              </div>
            )}
          </div>

          {/* Right Panel - Image Display */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E7E5E4] rounded-lg overflow-hidden min-h-[500px] flex items-center justify-center">
              <AnimatePresence mode="wait">
                {generating ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center p-12"
                  >
                    <div className="relative mx-auto w-20 h-20 mb-6">
                      <div className="absolute inset-0 rounded-full border-4 border-[#E7E5E4]" />
                      <div className="absolute inset-0 rounded-full border-4 border-t-[#0F3D2E] animate-spin" />
                    </div>
                    <h3 className="text-xl font-medium mb-2">Creating Your Visualisation</h3>
                    <p className="text-[#57534E] text-sm">
                      Our AI is fitting a {selectedMaterial?.name} sill to your window...
                    </p>
                  </motion.div>
                ) : resultImage ? (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full"
                  >
                    <div className="p-2">
                      <p className="text-center text-sm text-[#57534E] mb-2">
                        AI Visualisation - {selectedMaterial?.name} Window Sill
                      </p>
                      <img
                        src={resultImage}
                        alt="Visualisation result"
                        className="w-full h-auto rounded"
                        data-testid="result-image"
                      />
                    </div>
                  </motion.div>
                ) : uploadedImage ? (
                  <motion.div
                    key="uploaded"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full"
                  >
                    <div className="p-2">
                      <p className="text-center text-sm text-[#57534E] mb-2">Your Window Photo</p>
                      <img
                        src={uploadedImage}
                        alt="Uploaded window"
                        className="w-full h-auto rounded"
                        data-testid="uploaded-image"
                      />
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="placeholder"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center p-12"
                  >
                    <Camera className="w-16 h-16 mx-auto mb-4 text-[#D6D3D1]" />
                    <h3 className="text-xl font-medium text-[#A8A29E] mb-2">No Photo Yet</h3>
                    <p className="text-sm text-[#D6D3D1] max-w-sm mx-auto">
                      Upload a photo of your window to see how a stone sill would look
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* How it works */}
            {!resultImage && !generating && (
              <div className="mt-6 grid grid-cols-3 gap-4">
                {[
                  { step: "1", title: "Upload", desc: "Take or upload a photo of your window" },
                  { step: "2", title: "Choose", desc: "Select your preferred stone colour" },
                  { step: "3", title: "Visualise", desc: "AI generates a realistic preview" },
                ].map((item) => (
                  <div key={item.step} className="bg-white border border-[#E7E5E4] p-4 rounded-lg text-center">
                    <div className="w-8 h-8 rounded-full bg-[#0F3D2E] text-white flex items-center justify-center mx-auto mb-2 text-sm font-medium">
                      {item.step}
                    </div>
                    <h4 className="font-medium text-sm">{item.title}</h4>
                    <p className="text-xs text-[#57534E] mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Visualiser;
